import {
  EventSubtype,
  FlightLogsEvent,
  EventType
} from '../../common/api/spidertracks-sdk/types/FlightLogs';

export const getFilteredEvents = (
  startEventSubtype: EventSubtype,
  endEventSubtype: EventSubtype,
  events: FlightLogsEvent[]
) => {
  let previousEvent: FlightLogsEvent | null = null;
  const startEventIdToEndEventIdMap = new Map<string, string>();
  const endEventIdToStartEventIdMap = new Map<string, string>();
  const eventIdToEventMap = new Map<string, FlightLogsEvent>();
  const unpairedStartEvents = new Set<string>();

  for (const event of events) {
    eventIdToEventMap.set(event.eventId, event);

    // Track start events that don't have a pair yet
    if (event.eventSubtype === startEventSubtype) {
      unpairedStartEvents.add(event.eventId);
    }

    if (
      previousEvent &&
      previousEvent.eventSubtype === startEventSubtype &&
      event.eventSubtype === endEventSubtype
    ) {
      startEventIdToEndEventIdMap.set(previousEvent.eventId, event.eventId);
      endEventIdToStartEventIdMap.set(event.eventId, previousEvent.eventId);
      unpairedStartEvents.delete(previousEvent.eventId);
    }

    if (
      previousEvent &&
      previousEvent.eventSubtype === endEventSubtype &&
      event.eventSubtype === endEventSubtype
    ) {
      if (!event.isBackfill) {
        const startEventId = endEventIdToStartEventIdMap.get(previousEvent.eventId);
        if (startEventId) {
          startEventIdToEndEventIdMap.set(startEventId, event.eventId);
          endEventIdToStartEventIdMap.delete(previousEvent.eventId);
          endEventIdToStartEventIdMap.set(event.eventId, startEventId);
        }
      }
    }

    previousEvent = event;
  }

  const eventsToReturn: FlightLogsEvent[] = [];

  // Add paired events
  for (const [startEventId, EndEventId] of startEventIdToEndEventIdMap.entries()) {
    eventsToReturn.push(eventIdToEventMap.get(startEventId)!);
    eventsToReturn.push(eventIdToEventMap.get(EndEventId)!);
  }

  // Add unpaired start events
  for (const startEventId of unpairedStartEvents) {
    eventsToReturn.push(eventIdToEventMap.get(startEventId)!);
  }

  return eventsToReturn;
};

export const getFilteredAndPairedEvents = (events: FlightLogsEvent[]) => {
  const backfillEventsAvailable = events.find(event => event.isBackfill);
  if (!backfillEventsAvailable) {
    return events;
  }

  //we've intentionally left out other event such as taxi because we don't want to show them in the UI
  const powerEvents = events
    .filter(event => event.eventType === EventType.POWER)
    .sort((a, b) => a.eventTime - b.eventTime);

  const engineEvents = events
    .filter(event => event.eventType === EventType.ENGINE)
    .sort((a, b) => a.eventTime - b.eventTime);

  const flightEvents = events
    .filter(
      event =>
        event.eventType === EventType.FLIGHT &&
        (event.eventSubtype === EventSubtype.Takeoff || event.eventSubtype === EventSubtype.Landing)
    )
    .sort((a, b) => a.eventTime - b.eventTime);

  const blocksEvents = events
    .filter(
      event =>
        (event.eventType === EventType.FLIGHT && event.eventSubtype === EventSubtype.BlocksOn) ||
        (event.eventType === EventType.FLIGHT && event.eventSubtype === EventSubtype.BlocksOff)
    )
    .sort((a, b) => a.eventTime - b.eventTime);

  const filteredEvents = [
    ...getFilteredEvents(EventSubtype.On, EventSubtype.Off, powerEvents),
    ...getFilteredEvents(EventSubtype.On, EventSubtype.Off, engineEvents),
    ...getFilteredEvents(EventSubtype.Takeoff, EventSubtype.Landing, flightEvents),
    ...getFilteredEvents(EventSubtype.BlocksOff, EventSubtype.BlocksOn, blocksEvents)
  ];

  return filteredEvents.sort((a, b) => a.eventTime - b.eventTime);
};
